import { AxiosError, AxiosRequestConfig } from "axios";
import { serialize } from "object-to-formdata";
import { useState } from "react";
import {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { useOnErrorNavigation } from "../hooks/useOnErrorNavigation";
import { Endpoints } from "../types";
import { ApiError, memoirsApiInstance } from "./api";

type EnhancedRoute<Route extends keyof Endpoints> =
  Endpoints[Route]["PATH_PARAMS"] extends never
    ? Route
    : {
        route: Route;
        pathParams: Endpoints[Route]["PATH_PARAMS"];
      };

export const applyPathParams = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>
) => {
  if (typeof route === "string") return route;

  let finalRoute: string = route.route;
  Object.entries(route.pathParams).forEach(([key, value]) => {
    finalRoute = finalRoute.replace(`:${key}`, String(value));
  });

  return finalRoute;
};

export const keys = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>
) => ({
  all: [route] as const,
  //lists: [route, 'list'] as const,
  list: <T extends "paginated" | "infinite">(
    type: T,
    params?: T extends "infinite"
      ? Omit<Endpoints[Route]["INDEX"]["params"], "page">
      : Endpoints[Route]["INDEX"]["params"]
  ) => [route, "list", { type }, params] as const,
  //details: [route , 'detail'] as const,
  detail: (id: number | null | undefined) => [route, "detail", id] as const,
});

export const useIndexQuery = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  params?: Endpoints[Route]["INDEX"]["params"],
  options?: UseQueryOptions<
    Endpoints[Route]["INDEX"]["response"],
    AxiosError<ApiError>,
    Endpoints[Route]["INDEX"]["response"],
    readonly [
      EnhancedRoute<Route>,
      "list",
      { type: "paginated" },
      Endpoints[Route]["INDEX"]["params"] | undefined
    ]
  >
) => {
  const finalRoute = applyPathParams(route);

  return useQuery(
    keys(route).list("paginated", params),
    () =>
      memoirsApiInstance.get<Endpoints[Route]["INDEX"]["response"]>(
        finalRoute,
        {
          params,
        }
      ),
    { enabled: !!params, ...options }
  );
};

export const useShowQuery = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  id: number | undefined | null,
  options?: UseQueryOptions<
    Endpoints[Route]["SHOW"]["response"],
    AxiosError<ApiError>,
    Endpoints[Route]["SHOW"]["response"],
    readonly [EnhancedRoute<Route>, "detail", number | null | undefined]
  >,
  navigateOnError?: boolean
) => {
  const { handleErrorNavigation } = useOnErrorNavigation();
  const finalRoute = applyPathParams(route);

  return useQuery(
    keys(route).detail(id),
    () =>
      memoirsApiInstance.get<Endpoints[Route]["SHOW"]["response"]>(
        `${finalRoute}/${id}`
      ),
    {
      enabled: !!id,
      ...options,
      onError: (error) => {
        options?.onError?.(error);
        if (navigateOnError) handleErrorNavigation(error.response?.status);
      },
    }
  );
};

export const useCreateMutation = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  options?: UseMutationOptions<
    Endpoints[Route]["CREATE"]["response"],
    AxiosError<ApiError>,
    Endpoints[Route]["CREATE"]["params"]
  >,
  needsSerialization?: boolean,
  config?: AxiosRequestConfig
): UseMutationResult<
  Endpoints[Route]["CREATE"]["response"],
  AxiosError<ApiError>,
  Endpoints[Route]["CREATE"]["params"]
> => {
  const client = useQueryClient();
  const finalRoute = applyPathParams(route);

  return useMutation<
    Endpoints[Route]["CREATE"]["response"],
    AxiosError<ApiError>,
    Endpoints[Route]["CREATE"]["params"]
  >(
    (params) =>
      memoirsApiInstance.post<Endpoints[Route]["CREATE"]["response"]>(
        finalRoute,
        needsSerialization ? serialize(params) : params,
        config
      ),
    {
      ...options,
      onSuccess: (data, ...args) => {
        options?.onSuccess?.(data, ...args);
        client.invalidateQueries(keys(route).all);
      },
    }
  );
};

export const useUpdateMutation = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  options?: UseMutationOptions<
    Endpoints[Route]["UPDATE"]["response"],
    AxiosError<ApiError>,
    Endpoints[Route]["UPDATE"]["params"]
  >,
  needsSerialization?: boolean,
  config?: AxiosRequestConfig
): UseMutationResult<
  Endpoints[Route]["UPDATE"]["response"],
  AxiosError<ApiError>,
  Endpoints[Route]["UPDATE"]["params"]
> => {
  const client = useQueryClient();
  const finalRoute = applyPathParams(route);

  return useMutation<
    Endpoints[Route]["UPDATE"]["response"],
    AxiosError<ApiError>,
    Endpoints[Route]["UPDATE"]["params"]
  >(
    (params) =>
      memoirsApiInstance.patch<Endpoints[Route]["UPDATE"]["response"]>(
        finalRoute,
        needsSerialization ? serialize(params) : params,
        config
      ),
    {
      ...options,
      onSuccess: (data, ...args) => {
        client.invalidateQueries(keys(route).all);
        options?.onSuccess?.(data, ...args);
      },
    }
  );
};

export const useDestroyMutation = <Route extends keyof Endpoints>(
  route: EnhancedRoute<Route>,
  options?: UseMutationOptions<
    Endpoints[Route]["DESTROY"]["response"],
    AxiosError<ApiError>,
    number
  >
): UseMutationResult<
  Endpoints[Route]["DESTROY"]["response"],
  AxiosError<ApiError>,
  number
> => {
  const client = useQueryClient();
  const finalRoute = applyPathParams(route);

  return useMutation<
    Endpoints[Route]["DESTROY"]["response"],
    AxiosError<ApiError>,
    number
  >(
    (id) =>
      memoirsApiInstance.delete<Endpoints[Route]["DESTROY"]["response"]>(
        `${finalRoute}/${id}`
      ),
    {
      ...options,
      onSuccess: (data, ...args) => {
        // defer allows to call instructions before invalidation refetchs,
        // for example, allow navigation before refetch to prevent 404 errors)
        setTimeout(() => client.invalidateQueries(keys(route).all), 1);
        options?.onSuccess?.(data, ...args);
      },
    }
  );
};

export const useDownloadFile = () => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadFile = async (
    route: string,
    params?: AxiosRequestConfig["params"],
    filename?: string
  ) => {
    setIsDownloading(true);
    try {
      const response = await memoirsApiInstance.get(route, {
        params,
        responseType: "blob",
      });

      let blob: Blob;
      if (response instanceof Blob) {
        blob = response;
      } else if ((response as { data?: unknown }).data instanceof Blob) {
        blob = (response as { data: Blob }).data;
      } else {
        const blobPart: BlobPart =
          typeof (response as { data?: unknown }).data === "string"
            ? (response as { data: string }).data
            : (response as { data?: unknown }).data instanceof ArrayBuffer
            ? (response as { data: ArrayBuffer }).data
            : typeof response === "string"
            ? response
            : "";
        blob = new Blob([blobPart]);
      }

      if (!blob || blob.size === 0) {
        throw new Error("Le fichier téléchargé est vide ou invalide");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      let defaultFilename = "download.zip";

      if (blob.type === "application/zip") {
        defaultFilename = "archive.zip";
      } else if (blob.type.startsWith("image/")) {
        defaultFilename = `image.${blob.type.split("/")[1]}`;
      } else if (blob.type === "application/pdf") {
        defaultFilename = "document.pdf";
      }

      link.download = filename || defaultFilename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      return true;
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  return { downloadFile, isDownloading };
};
